import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReusableTable from "./../components/ReusableTable";
import { getApiUrl } from "./../utils/api";
import { BASE_URL } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BulkComboPage = () => {
  const UserId = "66abc123def4567890abcd12";

  const [subcategories, setSubcategories] = useState([]);
  const [bulkCombos, setBulkCombos] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch subcategories
  const fetchSubcategories = async () => {
    try {
      const res = await fetch(getApiUrl("subcategory-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userid: UserId }), // lowercase 'userid' as backend expects
      });
      const data = await res.json();
      if (data.success) setSubcategories(data.data || []);
    } catch (err) {
      toast.error("Failed to fetch subcategories!");
    }
  };

  // Fetch bulk combos
  const fetchBulkCombos = async () => {
    try {
      const res = await fetch(getApiUrl("bulk-combo-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: UserId }),
      });
      const data = await res.json();
      if (data.success) setBulkCombos(data.data || []);
    } catch (err) {
      toast.error("Failed to fetch bulk combos!");
    }
  };

  useEffect(() => {
    fetchSubcategories();
    fetchBulkCombos();
  }, []);

  // Handle delete
  const handleDelete = async (id) => {
    const res = await fetch(getApiUrl("bulk-combo-delete"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ comboId: id }),
    });
    const result = await res.json();
    if (result.success) {
      toast.success("Deleted Successfully!");
      fetchBulkCombos();
    }
  };

  // Open modal for add
  const openAddModal = () => {
    setEditId(null);
    formik.resetForm();
    setShowModal(true);
  };

  // Open modal for edit
  const openEditModal = (combo) => {
    setEditId(combo._id);
    formik.setFieldValue("title", combo.title);
    formik.setFieldValue("subcategoryId", combo.subcategoryId?._id || "");
    formik.setFieldValue("status", combo.status);
    formik.setFieldValue("validFrom", combo.validFrom?.split("T")[0] || "");
    formik.setFieldValue("validTo", combo.validTo?.split("T")[0] || "");
    formik.setFieldValue(
      "priceSlabs",
      combo.priceSlabs.map((slab) => ({
        minQuantity: slab.minQuantity,
        maxQuantity: slab.maxQuantity || "",
        price: slab.price,
      }))
    );
    setShowModal(true);
  };

  // Formik
  const formik = useFormik({
    initialValues: {
      title: "",
      subcategoryId: "",
      priceSlabs: [{ minQuantity: 1, maxQuantity: "", price: "" }],
      status: 1,
      validFrom: "",
      validTo: "",
    },

    validationSchema: Yup.object({
      title: Yup.string().required("Title required"),
      subcategoryId: Yup.string().required("Select subcategory"),
      priceSlabs: Yup.array()
        .of(
          Yup.object().shape({
            minQuantity: Yup.number().required("Min quantity required"),
            price: Yup.number().required("Price required"),
          })
        )
        .min(1, "Add at least one price slab"),
    }),

    onSubmit: async (values) => {
      const url = editId
        ? getApiUrl("bulk-combo-update")
        : getApiUrl("bulk-combo-add");

      const body = editId
        ? { comboId: editId, ...values, userId: UserId }
        : { ...values, userId: UserId };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const result = await res.json();
      if (result.success) {
        toast.success(editId ? "Updated Successfully!" : "Added Successfully!");
        setShowModal(false);
        fetchBulkCombos();
      } else {
        toast.error(result.message || "Error occurred!");
      }
    },
  });

  // Add new price slab row
  const addPriceSlab = () => {
    formik.setFieldValue("priceSlabs", [
      ...formik.values.priceSlabs,
      { minQuantity: "", maxQuantity: "", price: "" },
    ]);
  };

  // Remove price slab row
  const removePriceSlab = (index) => {
    const newSlabs = [...formik.values.priceSlabs];
    newSlabs.splice(index, 1);
    formik.setFieldValue("priceSlabs", newSlabs);
  };

  // Update price slab value
  const handlePriceSlabChange = (index, field, value) => {
    const newSlabs = [...formik.values.priceSlabs];
    newSlabs[index][field] = value;
    formik.setFieldValue("priceSlabs", newSlabs);
  };

  // Table columns
  const columns = [
    { name: "Title", selector: (row) => row.title },
    {
      name: "Subcategory",
      selector: (row) => row.subcategoryId?.name || "-",
    },
    {
      name: "Price Slabs",
      cell: (row) =>
        row.priceSlabs
          .map(
            (slab) =>
              `${slab.minQuantity}${
                slab.maxQuantity ? "-" + slab.maxQuantity : ""
              } → ₹${slab.price}`
          )
          .join(", "),
    },
    {
      name: "Status",
      selector: (row) => (row.status === 1 ? "Active" : "Inactive"),
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <button
            className="btn btn-warning btn-sm me-2"
            onClick={() => openEditModal(row)}
          >
            <i className="fa-regular fa-pen-to-square"></i>
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDelete(row._id)}
          >
            <i className="fa-regular fa-trash-can"></i>
          </button>
        </>
      ),
    },
  ];

  return (
    <div className="container mt-4">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="d-flex justify-content-between mb-3">
        <h3 className="fw-bold">Bulk Combo Management</h3>
        <button className="btn btn-primary" onClick={openAddModal}>
          + Add Bulk Combo
        </button>
      </div>

      <ReusableTable columns={columns} data={bulkCombos} title="Bulk Combo List" />

      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content rounded-4">
              <div className="modal-header">
                <h4 className="modal-title fw-bold">
                  {editId ? "Edit Bulk Combo" : "Add Bulk Combo"}
                </h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>

              <form onSubmit={formik.handleSubmit}>
                <div className="modal-body">
                  <div className="floating-label-group mb-2">
                    <input
                      type="text"
                      name="title"
                      placeholder="Combo Title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      required
                    />
                    <label>Combo Title</label>
                  </div>

                  <div className="mb-3">
                    <label>Select Subcategory</label>
                    <select
                      className="form-select"
                      name="subcategoryId"
                      value={formik.values.subcategoryId}
                      onChange={formik.handleChange}
                      required
                    >
                      <option value="">-- Select Subcategory --</option>
                      {subcategories.map((sub) => (
                        <option key={sub._id} value={sub._id}>
                          {sub.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label>Price Slabs</label>
                    {formik.values.priceSlabs.map((slab, index) => (
                      <div key={index} className="d-flex mb-2 gap-2">
                        <input
                          type="number"
                          placeholder="Min Qty"
                          className="form-control"
                          value={slab.minQuantity}
                          onChange={(e) =>
                            handlePriceSlabChange(index, "minQuantity", e.target.value)
                          }
                          required
                        />
                        <input
                          type="number"
                          placeholder="Max Qty (optional)"
                          className="form-control"
                          value={slab.maxQuantity}
                          onChange={(e) =>
                            handlePriceSlabChange(index, "maxQuantity", e.target.value)
                          }
                        />
                        <input
                          type="number"
                          placeholder="Price"
                          className="form-control"
                          value={slab.price}
                          onChange={(e) =>
                            handlePriceSlabChange(index, "price", e.target.value)
                          }
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => removePriceSlab(index)}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm mt-2"
                      onClick={addPriceSlab}
                    >
                      + Add Price Slab
                    </button>
                  </div>

                  <div className="mb-3">
                    <label>Status</label>
                    <select
                      className="form-select"
                      name="status"
                      value={formik.values.status}
                      onChange={formik.handleChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>

                  <div className="mb-3 d-flex gap-2">
                    <div className="flex-fill">
                      <label>Valid From</label>
                      <input
                        type="date"
                        className="form-control"
                        name="validFrom"
                        value={formik.values.validFrom}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="flex-fill">
                      <label>Valid To</label>
                      <input
                        type="date"
                        className="form-control"
                        name="validTo"
                        value={formik.values.validTo}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    {editId ? "Update Bulk Combo" : "Add Bulk Combo"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkComboPage;
